import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  TrendingUp,
  ShieldCheck,
  Leaf,
} from "lucide-react";
import { sellerAPI } from "../API/axios";
import { useAuth } from "../context/AuthContext";

interface FormErrors {
  email?: string;
  password?: string;
}

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [form, setForm] = useState({ email: "", password: "" });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // Show success message if redirected after registration/email-verify
  useEffect(() => {
    if (location.state?.verified) {
      toast.success("Email verified! Please login to continue.");
    }
    if (location.state?.fromRegister) {
      toast.info(
        "Registration successful! Please verify your email before logging in.",
      );
    }
  }, [location.state]);

  const validate = () => {
    const e: FormErrors = {};
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))
      e.email = "Valid email required";
    if (!form.password) e.password = "Password is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // In your login page component
  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await sellerAPI.login({
        email: form.email,
        password: form.password,
      });

      const {
        accessToken,
        refreshToken,
        emailVerified,
        sellerProfile,
        ...rest
      } = res.data;

      // Check if email is verified
      if (emailVerified === false) {
        sessionStorage.setItem("pendingVerificationEmail", form.email);
        toast.info("Please verify your email before logging in");
        navigate("/otp-verification", {
          state: {
            email: form.email,
            from: "login",
          },
        });
        return;
      }

      // Combine all data into one object
      const profileData = {
        ...rest,
        email: form.email,
        emailVerified,
        sellerProfile,
      };

      // Login with the complete data
      login(profileData, { accessToken, refreshToken });

      toast.success("Welcome back! 🎉");
      navigate("/dashboard");
    } catch (err: any) {
    } finally {
      setLoading(false);
    }
  };

  const set =
    (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((p) => ({ ...p, [field]: e.target.value }));

  return (
    <div className="min-h-screen bg-white relative flex flex-col page-root">
      {/* Background Decor */}
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute top-0 left-0 w-1/2 h-1/2 bg-primary/5 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-1/2 h-1/2 bg-secondary/3 rounded-full blur-[120px] translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {/* Left panel - Hero Side */}
        <div className="hidden lg:flex flex-col p-12 bg-slate-50 border-r border-slate-100">
          {/* <div className="flex items-center gap-4 mb-20 group">
            <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center text-white shadow-xl shadow-primary/20 group-hover:scale-105 transition-transform">
              <Leaf size={26} />
            </div>
            <div className="flex flex-col">
              <div className="font-display text-xl font-bold bg-gradient-to-br from-primary to-secondary bg-clip-text text-transparent">
                Harappa bioscience
              </div>
              <div className="text-xs text-slate-400 tracking-wider font-medium uppercase">
                Seller Portal
              </div>
            </div>
          </div> */}

          <div className="grow flex flex-col justify-center -mt-10">
            <h2 className="font-display text-4xl font-extrabold leading-tight tracking-tight text-slate-900 mb-10">
              Welcome back!
              <br />
              <span className="gradient-text">Your dashboard awaits.</span>
            </h2>

            <div className="flex flex-col gap-8 max-w-md">
              {[
                {
                  icon: TrendingUp,
                  label: "Track orders & revenue in real time",
                  bg: "bg-emerald-50",
                  text: "text-emerald-600",
                },
                {
                  icon: ShieldCheck,
                  label: "Manage GST-verified product listings",
                  bg: "bg-primary/5",
                  text: "text-primary",
                },
                {
                  icon: ArrowRight,
                  label: "Add products and go live instantly",
                  bg: "bg-amber-50",
                  text: "text-amber-600",
                },
              ].map(({ icon: Icon, label, bg, text }) => (
                <div key={label} className="flex items-center gap-5 group">
                  <div
                    className={`w-12 h-12 shrink-0 ${bg} rounded-xl flex items-center justify-center ${text} group-hover:scale-110 transition-transform`}
                  >
                    <Icon size={20} />
                  </div>
                  <span className="text-base font-medium text-slate-600 group-hover:text-slate-900 transition-colors">
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 mt-auto text-xs font-semibold text-slate-400 uppercase tracking-widest">
            <ShieldCheck size={16} className="text-emerald-500" />
            <span>Encrypted · ISO 27001 · SOC 2 Compliant</span>
          </div>
        </div>

        {/* Right panel - Form Side */}
        <div className="flex items-center justify-center p-6 sm:p-8 sm:pt-28 lg:pt-12 mt-12">
          <div className="w-full max-w-md animate-slide-up">
            <div className="mb-10 text-center lg:text-left">
              <h1 className="font-display text-3xl font-extrabold text-slate-900 mb-3 tracking-tight">
                Seller Login
              </h1>
              <p className="text-slate-500 text-sm leading-relaxed">
                Access your seller account to manage products, orders and
                payouts.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">
                  Email Address
                </label>
                <div className="relative group">
                  <Mail
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors"
                  />
                  <input
                    id="login-email"
                    type="email"
                    className="form-input !pl-12"
                    placeholder="seller@example.com"
                    value={form.email}
                    onChange={set("email")}
                  />
                </div>
                {errors.email && (
                  <span className="text-xs font-medium text-red-500 ml-1 mt-1">
                    {errors.email}
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest ml-1">
                  Password
                </label>
                <div className="relative group">
                  <Lock
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary transition-colors"
                  />
                  <input
                    id="login-password"
                    type={showPass ? "text" : "password"}
                    className="form-input !px-12"
                    placeholder="Your password"
                    value={form.password}
                    onChange={set("password")}
                  />
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-lg text-slate-400 hover:text-primary hover:bg-primary/5 transition-all"
                    onClick={() => setShowPass(!showPass)}
                  >
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.password && (
                  <span className="text-xs font-medium text-red-500 ml-1 mt-1">
                    {errors.password}
                  </span>
                )}
              </div>

              <div className="flex justify-end">
                <a
                  href="#"
                  className="text-xs font-semibold text-slate-400 hover:text-primary transition-colors"
                >
                  Forgot password?
                </a>
              </div>

              <button
                id="login-submit"
                type="submit"
                className="btn btn-primary w-full py-4 text-sm mt-2 font-bold tracking-wide"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Signing in...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span>Login to Dashboard</span>
                    <ArrowRight size={18} />
                  </div>
                )}
              </button>
            </form>

            <div className="relative my-10 flex items-center gap-4 text-slate-400 uppercase tracking-[0.2em] text-[10px] font-bold after:content-[''] after:h-px after:bg-slate-100 after:grow before:content-[''] before:h-px before:bg-slate-100 before:grow">
              or
            </div>

            <p className="text-center text-sm font-medium text-slate-500 -mt-6">
              New seller?{" "}
              <Link
                to="/register"
                className="text-primary font-bold hover:underline underline-offset-4"
              >
                Create an account
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
